import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.97] [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.18),0_4px_14px_-4px_hsl(var(--primary)/0.45)] hover:bg-primary/92 hover:shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.22),0_6px_20px_-4px_hsl(var(--primary)/0.5)]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.15),0_4px_14px_-4px_hsl(var(--destructive)/0.4)] hover:bg-destructive/92",
        outline:
          "border border-border/60 bg-[hsl(var(--glass-bg)/0.55)] backdrop-blur-md backdrop-saturate-150 hover:bg-[hsl(var(--glass-bg)/0.85)] hover:border-border shadow-[inset_0_1px_0_0_hsl(var(--glass-highlight)/0.5)]",
        secondary:
          "bg-[hsl(var(--glass-bg)/0.6)] backdrop-blur-md backdrop-saturate-150 text-secondary-foreground border border-border/40 hover:bg-[hsl(var(--glass-bg)/0.85)] shadow-[inset_0_1px_0_0_hsl(var(--glass-highlight)/0.5)]",
        ghost: "hover:bg-accent/60 hover:backdrop-blur-md hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-lg px-3",
        lg: "h-11 rounded-xl px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
